/**
 * Solr Search widget component.
 * @module components/theme/SearchWidget/SearchWidget
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { Form } from 'semantic-ui-react';
import { compose } from 'redux';
import { defineMessages, injectIntl, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { solrSearchSuggestions } from '@kitconcept/volto-solr/actions';
import Autosuggest from 'react-autosuggest';
import cx from 'classnames';

import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import qs from 'query-string';
import config from '@plone/volto/registry';

import searchSVG from '@kitconcept/volto-solr/icons/search.svg';
import debounce from 'lodash.debounce';

const messages = defineMessages({
  search: {
    id: 'Search',
    defaultMessage: 'Search',
  },
  searchSite: {
    id: 'Search Site',
    defaultMessage: 'Search Site',
  },
  showAllSearchResults: {
    id: 'Show all search results...',
    defaultMessage: 'Show all search results...',
  },
});

// --
// SolrSearchAutosuggest is factored ouf from SolrSearchWidget, and it contains
// only the input field, so we can include it better from Search.jsx.
// --

const ID_ALL = '@ALL';
// Items to show. Note this must be <= 10 as 10 items will
// always be fetched currently.
const NR_ITEMS = 8;

const SolrSearchAutosuggestRaw = (props) => {
  const originalText = props.value;
  const dispatch = useDispatch();
  let suggestions = useSelector((state) =>
    state.solrSearchSuggestions.items.slice(0, NR_ITEMS),
  ).concat({
    '@type': 'ShowAll',
    '@id': ID_ALL,
    title: originalText,
  });

  const renderSuggestion = (suggestion) => {
    const typeLiteralMap = config.settings.typeLiteralMap;

    return (
      <>
        {suggestion['@type'] === 'ShowAll' && suggestions.length > 1 ? (
          <button className="all-results-button">
            <span>Alle Suchergebnisse anzeigen</span>
          </button>
        ) : (
          suggestion['@type'] !== 'ShowAll' && (
            <div
              className="suggestion"
              to={flattenToAppURL(suggestion['@id']).split('/')}
            >
              <div className="suggestion-wrapper">
                <div className="suggestion-title">
                  <span>{typeLiteralMap(suggestion['@type'])}: </span>
                  {suggestion.display_rezeptcode && (
                    <b>{suggestion.display_rezeptcode} </b>
                  )}{' '}
                  {suggestion.title}
                </div>
              </div>
            </div>
          )
        )}
      </>
    );
  };

  const cancelRequest = useRef(null);
  const debounceCustom = useRef(
    debounce((value) => {
      if (cancelRequest.current) {
        cancelRequest.current.request.abort();
        cancelRequest.current = null;
      }
      cancelRequest.current = dispatch(solrSearchSuggestions(value));
    }, 300),
  );

  const onSuggestionsFetchRequested = ({ value, reason }) => {
    debounceCustom.current(value);

    if (reason === 'suggestion-selected') {
      props.history.push(flattenToAppURL(value['@id']));
    }
  };

  const getSuggestionValue = (suggestion) => suggestion;

  const onSubmit = (event) => {
    if (props.inputRef) {
      //    props.inputRef.current.value = '';
      // Only search when text is provided - do not navigate to empty search results.
      // Important: Blur the input, otherwise the dropdown will reopen and never close.
      props.inputRef.current.blur();
    }
    if (props.onSubmit) {
      props.onSubmit(event);
    }
    event.preventDefault();
  };

  const ref = useRef();

  // react-autosuggest does not seem to support an onSubmit handler
  const onKeyDown = (event) => {
    if (event.key === 'Enter') {
      // We want to onSubmit **ONLY** when enter is pressed in the input.
      // NOT when enter is pressed on any of the drowdown items, or on the
      // All Items line. This makes sure that the dialog disappears when
      // we pressed enter in the input, but pressing input on any other
      // line does the navigation as it should.
      const { highlightedSuggestionIndex } = ref.current?.state;
      if (highlightedSuggestionIndex == null) {
        onSubmit(event);
        return false;
      }
    }
    return true;
  };

  const onSuggestionSelected = (
    event,
    { suggestion, suggestionValue, suggestionIndex, sectionIndex, method },
  ) => {
    const id = suggestion['@id'];
    if (id === ID_ALL) {
      onSubmit(event);
      event.preventDefault();
    } else {
      props.onSelectSuggestion && props.onSelectSuggestion(event);
      props.history.push(flattenToAppURL(id));
      event.preventDefault();
    }
  };

  const inputProps = {
    placeholder: props.placeholder,
    value: props.value,
    onChange: props.onChange,
    onFocus: props.onFocus,
    onBlur: props.onBlur,
    onKeyDown: onKeyDown,
  };

  const storeInputReference = (autosuggest) => {
    if (autosuggest) {
      if (props.inputRef) {
        props.inputRef.current = autosuggest.input;
      }
      ref.current = autosuggest;
    }
  };

  return (
    <Autosuggest
      className="search-input"
      ref={storeInputReference}
      suggestions={suggestions || []}
      onSuggestionsFetchRequested={onSuggestionsFetchRequested}
      onSuggestionsClearRequested={() => {}}
      renderSuggestion={renderSuggestion}
      getSuggestionValue={getSuggestionValue}
      inputProps={inputProps}
      onSuggestionSelected={onSuggestionSelected}
      onSubmit={onSubmit}
    />
  );
};

export const SolrSearchAutosuggest = compose(
  withRouter,
  injectIntl,
)(SolrSearchAutosuggestRaw);

// --
// SolrSearchWidget would be needed if we wanted the search widget
// in the header of each page, as originally supported by Volto.
// --

const SolrSearchWidget = (props) => {
  const intl = useIntl();
  const [focused, setFocused] = useState(false);
  const chainedOnFocus = props?.onFocus;
  const onFocus = useCallback(() => {
    setFocused(true);
    if (chainedOnFocus) {
      chainedOnFocus();
    }
  }, [setFocused, chainedOnFocus]);
  const chainedOnBlur = props?.onBlur;
  const onBlur = useCallback(() => {
    setFocused(false);
    if (chainedOnBlur) {
      chainedOnBlur();
    }
  }, [setFocused, chainedOnBlur]);
  const [text, setText] = useState('');
  // const [originalText, setOriginalText] = useState('');

  const onChange = (event, { newValue, method }) => {
    // method === 'type' && setOriginalText(newValue);
    typeof newValue === 'string' ? setText(newValue) : setText(newValue.title);
  };

  useEffect(() => {
    if (props.location.pathname === '/search') {
      const SearchableText = qs.parse(props.location.search)?.SearchableText;
      if (SearchableText?.length > 0 && text.length === 0) {
        setText(SearchableText);
      }
    } else {
      setText('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.location.pathname]);

  const onSubmit = (event) => {
    // Only search when text is provided - do not navigate to empty search results.
    if (text) {
      props.history.push(
        `/search?SearchableText=${text}&metadata_fields=room&metadata_fields=building&metadata_fields=phone&metadata_fields=email&metadata_fields=getIcon&metadata_fields=institute`,
      );
    }
    event.preventDefault();
  };
  return (
    <div className={cx('search-widget', { focused })}>
      <Form action="/search" onSubmit={onSubmit} className="">
        <Form.Field className="searchbox">
          <SolrSearchAutosuggest
            {...props}
            value={text ? text : ''}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            onSubmit={onSubmit}
            placeholder={props.intl.formatMessage(messages.searchSite)}
          />
          <button aria-label={intl.formatMessage(messages.search)}>
            <Icon name={searchSVG} />
          </button>
        </Form.Field>
      </Form>
    </div>
  );
};

export default compose(withRouter, injectIntl)(SolrSearchWidget);
